import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { useLang } from "../../context/lang-context";
import LandingNav from "../../components/landing/LandingNav";
import LandingFooter from "../../components/landing/LandingFooter";
import "../../components/landing/landing.css";
import "./contact.css";

type FormState = {
  name: string;
  restaurant_name: string;
  phone: string;
  email: string;
  message: string;
};

const INITIAL_FORM: FormState = {
  name: "",
  restaurant_name: "",
  phone: "",
  email: "",
  message: "",
};

const API_BASE =
  process.env.REACT_APP_API_URL?.replace("/graphql", "") ||
  "https://api.fooapp.mx";

const ContactPage: React.FC = () => {
  const { t } = useLang();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.id]: e.target.value });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(
          data.error ||
            t(
              "Ocurrió un error. Intenta de nuevo.",
              "Something went wrong. Please try again."
            )
        );
      } else {
        setSuccess(true);
        setForm(INITIAL_FORM);
      }
    } catch {
      setError(
        t(
          "No se pudo conectar al servidor. Intenta de nuevo.",
          "Could not connect to the server. Please try again."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("Contacto | Foo", "Contact | Foo")}</title>
        <meta
          name="description"
          content={t(
            "Contáctanos para registrar tu restaurante en Foo App.",
            "Contact us to register your restaurant on Foo App."
          )}
        />
      </Helmet>
      <LandingNav />

      <section className="contact-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-md-7 col-lg-6">
              <h1 className="contact-title">
                {t("¿Quieres unirte a Foo?", "Want to join Foo?")}
              </h1>
              <p className="contact-subtitle">
                {t(
                  "Déjanos tus datos y nos pondremos en contacto contigo para registrar tu restaurante.",
                  "Leave us your info and we'll reach out to register your restaurant."
                )}
              </p>

              {success ? (
                <div className="contact-success">
                  <div className="contact-success-icon">
                    <i className="fas fa-check"></i>
                  </div>
                  <h2>{t("¡Listo!", "Done!")}</h2>
                  <p>
                    {t(
                      "Recibimos tu información. Nos pondremos en contacto contigo pronto.",
                      "We received your info. We'll be in touch soon."
                    )}
                  </p>
                  <button
                    className="btn btn-dark mt-2"
                    onClick={() => setSuccess(false)}
                  >
                    {t("Enviar otro mensaje", "Send another message")}
                  </button>
                </div>
              ) : (
                <form
                  className="contact-form"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <div className="contact-field">
                    <label htmlFor="name">{t("Tu nombre", "Your name")}</label>
                    <input
                      id="name"
                      type="text"
                      className="form-control"
                      placeholder={t("Nombre completo", "Full name")}
                      value={form.name}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="name"
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="restaurant_name">
                      {t("Nombre del restaurante", "Restaurant name")}
                    </label>
                    <input
                      id="restaurant_name"
                      type="text"
                      className="form-control"
                      placeholder={t(
                        "Nombre del restaurante",
                        "Restaurant name"
                      )}
                      value={form.restaurant_name}
                      onChange={handleChange}
                      required
                      maxLength={100}
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="phone">
                      {t("Teléfono (10 dígitos)", "Phone (10 digits)")}
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      className="form-control"
                      placeholder="5512345678"
                      value={form.phone}
                      onChange={handleChange}
                      required
                      maxLength={15}
                      autoComplete="tel"
                      inputMode="numeric"
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="email">
                      {t("Correo electrónico", "Email address")}
                    </label>
                    <input
                      id="email"
                      type="email"
                      className="form-control"
                      placeholder="correo@ejemplo.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      autoComplete="email"
                    />
                  </div>

                  <div className="contact-field">
                    <label htmlFor="message">
                      {t("Mensaje (opcional)", "Message (optional)")}
                    </label>
                    <textarea
                      id="message"
                      className="form-control"
                      placeholder={t(
                        "Cuéntanos sobre tu restaurante o cualquier pregunta que tengas...",
                        "Tell us about your restaurant or any questions you have..."
                      )}
                      value={form.message}
                      onChange={handleChange}
                      maxLength={300}
                      rows={4}
                    />
                    <small className="text-muted">
                      {form.message.length}/300
                    </small>
                  </div>

                  {error && (
                    <div className="contact-error">
                      <i className="fas fa-exclamation-circle mr-2"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn contact-submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm mr-2"
                          role="status"
                          aria-hidden="true"
                        />
                        {t("Enviando...", "Sending...")}
                      </>
                    ) : (
                      t("Enviar mensaje", "Send message")
                    )}
                  </button>
                </form>
              )}
            </div>

            <div className="col-12 col-md-4 col-lg-4 d-none d-md-flex contact-side">
              <div>
                <ContactInfo
                  icon="fas fa-envelope"
                  title={t("Email", "Email")}
                  text="contacto@fooapp.mx"
                  href="mailto:contacto@fooapp.mx"
                />
                <ContactInfo
                  icon="fab fa-whatsapp"
                  title="WhatsApp"
                  text={t("Escríbenos", "Message us")}
                  href="https://wa.me/526144316481"
                />
                <ContactInfo
                  icon="fas fa-globe"
                  title={t("Plataforma", "Platform")}
                  text="foopos.mx"
                  href="https://foopos.mx"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
    </>
  );
};

type ContactInfoProps = {
  icon: string;
  title: string;
  text: string;
  href: string;
};

const ContactInfo: React.FC<ContactInfoProps> = ({
  icon,
  title,
  text,
  href,
}) => (
  <a
    href={href}
    className="contact-info-item"
    target="_blank"
    rel="noopener noreferrer"
  >
    <div className="contact-info-icon">
      <i className={icon}></i>
    </div>
    <div>
      <div className="contact-info-title">{title}</div>
      <div className="contact-info-text">{text}</div>
    </div>
  </a>
);

export default ContactPage;
